import { create } from 'zustand';

import { DEFAULT_INPUT_STATUS } from '../../constants';

import type { TypeInputState } from '../../types/common.types';
import type {
  TypePickupAddressDataState,
  TypeReceiveDetailOrderItem,
  TypeReceiveItemErrorState,
  TypeReceiveRequestItemErrorState,
  TypeReceiveRequestOrderInfoFilter,
  TypeReceiveRequestOrderInfoFilterErrorState,
  TypeReceiveRequestRegisterModalFilter,
  TypeReceiveRequestRegisterModalGoods,
} from '../../types/receive.types';

export const DEFAULT_RECEIVE_REQUESTS_REGISTER_FILTER: TypeReceiveRequestRegisterModalFilter = {
  center: null,
  temperature: null,
  xDock: 'false',
  xDockCenter: null,
  supplierCode: null,
  note: '',
};

/** 출고지 정보 입력값 초기값 */
export const DEFAULT_PICKUP_ADDRESS_DATA: TypePickupAddressDataState = {
  address: DEFAULT_INPUT_STATUS,
  palletQuantity: DEFAULT_INPUT_STATUS,
  vehicleSize: DEFAULT_INPUT_STATUS,
};

/** 출고지 정보 입력값 validation 초기값 */
export const DEFAULT_RECEIVE_REQUEST_ORDER_INFO_FILTER_ERROR_STATUS: Omit<
  TypeReceiveRequestOrderInfoFilterErrorState,
  'orderId'
> = {
  releaseCode: DEFAULT_INPUT_STATUS,
  expectedDateTime: DEFAULT_INPUT_STATUS,
  expectedDate: DEFAULT_INPUT_STATUS,
  expectedTime: DEFAULT_INPUT_STATUS,
  kurlyPickupAddress: DEFAULT_INPUT_STATUS,
  milkrunPickupAddresses: DEFAULT_INPUT_STATUS,
  pickupAddressData: {
    kurlyDefault: DEFAULT_PICKUP_ADDRESS_DATA,
    kurlyEtc: DEFAULT_PICKUP_ADDRESS_DATA,
    milkrunDefault: DEFAULT_PICKUP_ADDRESS_DATA,
    milkrunEtc1: DEFAULT_PICKUP_ADDRESS_DATA,
    milkrunEtc2: DEFAULT_PICKUP_ADDRESS_DATA,
  },
};

type TypeRequestModalDefaultInfoStatus = {
  center: TypeInputState;
  temperature: TypeInputState;
  xDock: TypeInputState;
  supplierCode: TypeInputState;
};

type TypeReceiveStore = {
  /**
   * [입고요청관리] 입고요청 등록 모달 > 1페이지 > 기본 정보 > 입력값
   */
  requestModalDefaultInfoValue: TypeReceiveRequestRegisterModalFilter;
  setRequestModalDefaultInfoValue: (updatedValue: Partial<TypeReceiveRequestRegisterModalFilter>) => void;
  resetRequestModalDefaultInfoValue: () => void;
  /**
   * [입고요청관리] 입고요청 등록 모달 > 1페이지 > 기본 정보 > 입력값 validation 상태
   */
  requestModalDefaultInfoStatus: TypeRequestModalDefaultInfoStatus;
  setRequestModalDefaultInfoStatus: (updatedValue: Partial<TypeRequestModalDefaultInfoStatus>) => void;
  resetRequestModalDefaultInfoStatus: () => void;

  /**
   * [입고요청관리] 입고요청 등록 모달 > 1페이지 > 입고 상품 > 테이블 데이터
   */
  requestModalGoodsList: TypeReceiveRequestRegisterModalGoods[];
  setRequestModalGoodsList: (requestModalGoodsList: TypeReceiveRequestRegisterModalGoods[]) => void;
  /**
   * [입고요청관리] 입고요청 등록 모달 > 1페이지 > 입고 상품 > 테이블 데이터 > 입력값 validation 상태
   */
  requestModalGoodsListStatus: TypeReceiveRequestItemErrorState[];
  setRequestModalGoodsListStatus: (requestModalGoodsListStatus: TypeReceiveRequestItemErrorState[]) => void;
  resetRequestModalGoodsListStatus: () => void;

  /**
   * [입고요청관리] 입고요청 등록 모달 > 2페이지 > 입고 예정 정보 > 입력값
   */
  requestModalOrderInfoValue: TypeReceiveRequestOrderInfoFilter[];
  setRequestModalOrderInfoValue: (requestModalOrderInfoValue: TypeReceiveRequestOrderInfoFilter[]) => void;
  /**
   * [입고요청관리] 입고요청 등록 모달 > 2페이지 > 입고 예정 정보 > 입력값 validation 상태
   */
  requestModalOrderInfoStatus: TypeReceiveRequestOrderInfoFilterErrorState[];
  setRequestModalOrderInfoStatus: (requestModalOrderInfoStatus: TypeReceiveRequestOrderInfoFilterErrorState[]) => void;

  /**
   * [입고요청관리] 입고요청 상세 모달 > 입고 상품
   */
  detailModalOrderGoodsList: TypeReceiveDetailOrderItem[];
  setDetailModalOrderGoodsList: (detailModalOrderGoodsList: TypeReceiveDetailOrderItem[]) => void;
  /**
   * [입고요청관리] 입고요청 상세 모달 > 입고 상품 > 입력값 validation 상태
   */
  detailModalOrderGoodsListStatus: TypeReceiveItemErrorState[];
  setDetailModalOrderGoodsListStatus: (detailModalOrderGoodsListStatus: TypeReceiveItemErrorState[]) => void;
  resetDetailModalOrderGoodsListStatus: () => void;
};

/**
 * 입고 관리 스토어
 */
export const useReceiveStore = create<TypeReceiveStore>((set, get) => ({
  requestModalDefaultInfoValue: DEFAULT_RECEIVE_REQUESTS_REGISTER_FILTER,
  setRequestModalDefaultInfoValue: (updatedValue) =>
    set((state) => ({
      requestModalDefaultInfoValue: { ...state.requestModalDefaultInfoValue, ...updatedValue },
    })),
  resetRequestModalDefaultInfoValue: () =>
    set({
      requestModalDefaultInfoValue: DEFAULT_RECEIVE_REQUESTS_REGISTER_FILTER,
    }),

  requestModalDefaultInfoStatus: {
    center: DEFAULT_INPUT_STATUS,
    temperature: DEFAULT_INPUT_STATUS,
    xDock: DEFAULT_INPUT_STATUS,
    supplierCode: DEFAULT_INPUT_STATUS,
  },
  setRequestModalDefaultInfoStatus: (updatedValue) =>
    set((state) => ({
      requestModalDefaultInfoStatus: { ...state.requestModalDefaultInfoStatus, ...updatedValue },
    })),
  resetRequestModalDefaultInfoStatus: () =>
    set({
      requestModalDefaultInfoStatus: {
        center: DEFAULT_INPUT_STATUS,
        temperature: DEFAULT_INPUT_STATUS,
        xDock: DEFAULT_INPUT_STATUS,
        supplierCode: DEFAULT_INPUT_STATUS,
      },
    }),

  requestModalGoodsList: [],
  setRequestModalGoodsList: (requestModalGoodsList) => set({ requestModalGoodsList }),

  requestModalGoodsListStatus: [],
  setRequestModalGoodsListStatus: (requestModalGoodsListStatus) => set({ requestModalGoodsListStatus }),
  resetRequestModalGoodsListStatus: () => {
    const { requestModalGoodsList } = get();
    const initialErrors = requestModalGoodsList.map(() => ({
      date: DEFAULT_INPUT_STATUS.status,
      boxQty: DEFAULT_INPUT_STATUS.status,
      boxPerQty: DEFAULT_INPUT_STATUS.status,
      eachQty: DEFAULT_INPUT_STATUS.status,
    }));
    set({ requestModalGoodsListStatus: initialErrors });
  },

  requestModalOrderInfoValue: [],
  setRequestModalOrderInfoValue: (requestModalOrderInfoValue) => set({ requestModalOrderInfoValue }),

  requestModalOrderInfoStatus: [],
  setRequestModalOrderInfoStatus: (requestModalOrderInfoStatus) => set({ requestModalOrderInfoStatus }),

  detailModalOrderGoodsList: [],
  setDetailModalOrderGoodsList: (detailModalOrderGoodsList) => set({ detailModalOrderGoodsList }),

  detailModalOrderGoodsListStatus: [],
  setDetailModalOrderGoodsListStatus: (detailModalOrderGoodsListStatus) => set({ detailModalOrderGoodsListStatus }),
  resetDetailModalOrderGoodsListStatus: () => {
    const { detailModalOrderGoodsList } = get();
    const initialErrors = detailModalOrderGoodsList.map(() => ({
      date: DEFAULT_INPUT_STATUS.status,
      boxQty: DEFAULT_INPUT_STATUS.status,
      boxPerQty: DEFAULT_INPUT_STATUS.status,
      eachQty: DEFAULT_INPUT_STATUS.status,
    }));
    set({ detailModalOrderGoodsListStatus: initialErrors });
  },
}));
